import { useEffect, useRef, useState } from 'react';
import { useProductMutations } from './api';
import { useToast } from '../components/Toast';
import { ApiRequestError } from '../lib/api';
import { ProductThumb } from '../visitor/ProductThumb';
import type { Product } from '../lib/types';

const ACCEPT = 'image/jpeg,image/png,image/webp';
const MAX_PER_UPLOAD = 10;

/**
 * Live photo manager for an existing product — used in the edit modal.
 * Uploads immediately, supports set-primary and remove.
 */
export function ProductPhotoManager({
  product,
  onChanged,
}: {
  product: Product;
  onChanged?: () => void;
}) {
  const { addImages, deleteImage, setPrimaryImage } = useProductMutations();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const images = [...(product.images ?? [])].sort((a, b) => a.order - b.order);
  const busy = addImages.isPending || deleteImage.isPending || setPrimaryImage.isPending;

  const pick = () => inputRef.current?.click();

  const onFiles = async (list: FileList | null) => {
    if (!list?.length) return;
    const files = Array.from(list).slice(0, MAX_PER_UPLOAD);
    try {
      await addImages.mutateAsync({ id: product._id, files });
      toast(`Added ${files.length} photo${files.length === 1 ? '' : 's'}`, 'success');
      onChanged?.();
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Photo upload failed', 'error');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const makePrimary = async (imageId?: string) => {
    if (!imageId) return;
    try {
      await setPrimaryImage.mutateAsync({ id: product._id, imageId });
      onChanged?.();
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Could not set primary', 'error');
    }
  };

  const remove = async (imageId?: string) => {
    if (!imageId) return;
    try {
      await deleteImage.mutateAsync({ id: product._id, imageId });
      toast('Photo removed', 'success');
      onChanged?.();
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Could not remove photo', 'error');
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="label">Photos</span>
        <button type="button" className="btn-outline px-2 py-1 text-xs" onClick={pick} disabled={busy}>
          {addImages.isPending ? 'Uploading…' : 'Add photos'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => onFiles(e.target.files)}
        />
      </div>

      {images.length === 0 ? (
        <p className="rounded-lg border border-dashed border-ink-200 px-3 py-4 text-center text-xs text-ink-400">
          No photos yet. JPG, PNG or WEBP.
        </p>
      ) : (
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
          {images.map((img) => (
            <div
              key={img._id ?? img.url}
              className="group relative aspect-square overflow-hidden rounded-md border border-ink-100 bg-ink-50"
            >
              <ProductThumb src={img.thumbnailUrl ?? img.url} alt="product photo" />
              {img.isPrimary && (
                <span className="absolute left-1 top-1 rounded bg-ink-900/80 px-1 text-[9px] font-medium uppercase tracking-wide text-white">
                  Primary
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-ink-900/70 px-1 py-0.5 opacity-0 transition group-hover:opacity-100">
                {!img.isPrimary && (
                  <button
                    type="button"
                    className="text-[9px] text-white hover:underline"
                    onClick={() => makePrimary(img._id)}
                    disabled={busy}
                  >
                    Make primary
                  </button>
                )}
                <button
                  type="button"
                  className="ml-auto text-[9px] text-red-300 hover:underline"
                  onClick={() => remove(img._id)}
                  disabled={busy}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Local file picker with previews — used when creating a product that does not
 * exist yet. Selected files are held by the parent and uploaded after create.
 */
export function PendingPhotoPicker({
  files,
  onChange,
}: {
  files: File[];
  onChange: (files: File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<string[]>([]);

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const add = (list: FileList | null) => {
    if (!list?.length) return;
    onChange([...files, ...Array.from(list)].slice(0, MAX_PER_UPLOAD));
    if (inputRef.current) inputRef.current.value = '';
  };
  const removeAt = (i: number) => onChange(files.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="label">Photos (optional)</span>
        <button
          type="button"
          className="btn-outline px-2 py-1 text-xs"
          onClick={() => inputRef.current?.click()}
        >
          Choose photos
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => add(e.target.files)}
        />
      </div>
      {files.length === 0 ? (
        <p className="rounded-lg border border-dashed border-ink-200 px-3 py-4 text-center text-xs text-ink-400">
          JPG, PNG or WEBP. The first photo becomes the primary image.
        </p>
      ) : (
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
          {files.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="group relative aspect-square overflow-hidden rounded-md border border-ink-100 bg-ink-50"
            >
              <img src={previews[i]} alt={f.name} className="h-full w-full object-cover" />
              {i === 0 && (
                <span className="absolute left-1 top-1 rounded bg-ink-900/80 px-1 text-[9px] font-medium uppercase tracking-wide text-white">
                  Primary
                </span>
              )}
              <button
                type="button"
                className="absolute inset-x-0 bottom-0 bg-ink-900/70 py-0.5 text-[9px] text-red-300 opacity-0 transition hover:underline group-hover:opacity-100"
                onClick={() => removeAt(i)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
